import { useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import { formatDateTime, formatKrw } from '@/lib/format';
import { AccountNav } from './AccountNav';
import { StockLink } from './StockLink';
import {
  DECISION_LABEL,
  MODE_DESCRIPTION,
  MODE_LABEL,
  decisionTone,
} from './autoTradeTypes';
import type { AutoTradeConfig, AutoTradeOrder } from './autoTradeTypes';
import { useAutoTradeConfig, useAutoTradeOrders } from './useAutoTrade';

/**
 * 자동매매 화면. (2026-09-20, BACKLOG I-5 A3)
 *
 * 🔴 지금은 **보기만** 하는 화면이다
 *   모드를 올리려면 재인증(패스키·OTP)이 필요한데 그것이 아직 없다(A5).
 *   재인증 없이 모드를 바꾸는 버튼을 먼저 만들면, 나중에 재인증을 붙여도
 *   그 우회로가 남는다. (autoTradeApi 주석)
 *
 * 🔴 무엇을 보여주나 — "왜 안 샀나" 가 "왜 샀나" 만큼 중요하다
 *   한도에 막힌 것(REJECTED)도 그대로 보여준다. 성공한 것만 보이면
 *   "신호가 없었다" 와 "신호는 있었는데 막혔다" 가 똑같아 보인다.
 *
 * 🔴 이 화면은 TRADING_ACCOUNT 권한이다
 *   내 실계좌를 움직이는 설정이라 가족에게 열지 않는다. (AccountPage 와 같은 원칙)
 */
export function AutoTradePage() {
  const [days, setDays] = useState(7);
  const config = useAutoTradeConfig('KR');
  const orders = useAutoTradeOrders(days);

  return (
    <div>
      <AccountNav />

      <section className="px-4 py-4">
        <h2 className="mb-1 text-base font-semibold text-fg">자동매매</h2>
        <p className="mb-3 text-xs text-fg-subtle">
          모의투자 계좌가 사고팔 때, 내 계좌라면 어떤 주문이 나갔을지 기록합니다.
        </p>

        {config.isLoading && <Spinner label="설정을 불러오는 중" />}
        {config.isError && (
          <ErrorState
            message={config.error?.message ?? '설정을 불러오지 못했습니다.'}
            onRetry={() => void config.refetch()}
          />
        )}
        {config.data === null && !config.isLoading && <NotConfigured />}
        {config.data && <ConfigCard config={config.data} />}
      </section>

      <section className="border-t border-line px-4 py-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="flex-1 text-sm font-semibold text-fg">주문 시도 기록</h3>
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={cx(
                'rounded-full px-3 py-1 text-xs font-medium',
                d === days ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
              )}
            >
              {d}일
            </button>
          ))}
        </div>

        <p className="mb-3 text-xs text-fg-subtle">
          한도나 안전장치에 막힌 것도 사유와 함께 보여줍니다. 그래야 &ldquo;신호가 없었다&rdquo;와
          &ldquo;신호는 있었는데 막혔다&rdquo;를 구분할 수 있습니다.
        </p>

        {/*
          🔴 9/21 은 기록이 불완전한 날이다 (docs/impl/123 7절)
            1차 매수에 기록 호출이 빠져 있어 그날 매수의 약 15%만 남았다.
            그 행들이 7~30일 필터 안에 계속 보이므로, 표를 보고 "그림자가
            모의보다 훨씬 적게 샀다" 로 읽지 않도록 화면에 적어 둔다.
            🔴 소급 기록은 하지 않았다 - 나중에 채운 행은 그때의 판단이 아니다.
            2026-10-22 가 되면 30일 필터에서도 사라지므로 이 안내를 지운다 (BACKLOG)
            ✏️ 2026-09-28 정정 - 전에 "09-28" 이라 적었는데, 조회는 지금 - days 라 30일 필터는 10-21 까지 9/21 을 보여준다
        */}
        <p className="mb-3 rounded-md bg-surface-hover px-3 py-2 text-xs text-fg-muted">
          <strong className="font-semibold text-fg">9월 21일 기록은 불완전합니다.</strong>{' '}
          첫 매수(1차)를 남기지 않는 결함이 있어 그날은 실제 매매의 일부만 기록됐습니다.
          같은 날 고쳤고, 모의투자와의 대조는 <strong className="font-semibold text-fg">9월 22일부터</strong>{' '}
          유효합니다. 지난 기록을 나중에 채워 넣지는 않았습니다 &mdash; 그러면 &ldquo;그때 실제로 그
          판단을 했다&rdquo;가 아니게 되기 때문입니다.
        </p>

        {orders.isLoading && <Spinner label="기록을 불러오는 중" />}
        {orders.isError && (
          <ErrorState
            message={orders.error?.message ?? '기록을 불러오지 못했습니다.'}
            onRetry={() => void orders.refetch()}
          />
        )}
        {orders.data && orders.data.length === 0 && (
          <EmptyState
            title="아직 기록이 없습니다"
            description="자동매매가 꺼져 있거나, 아직 매매 신호가 없었습니다."
          />
        )}
        {orders.data && orders.data.length > 0 && <OrderTable orders={orders.data} />}
      </section>
    </div>
  );
}

/** 설정이 아직 없을 때. 오류가 아니다. */
function NotConfigured() {
  return (
    <EmptyState
      title="아직 설정하지 않았습니다"
      description="자동매매는 꺼져 있습니다. 켜는 것은 관리자가 직접 설정을 만든 뒤에 가능합니다."
    />
  );
}

function ConfigCard({ config }: { config: AutoTradeConfig }) {
  return (
    <Card>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-lg font-semibold text-fg">{MODE_LABEL[config.mode]}</span>
        <span className="text-xs text-fg-muted">{config.sourceLabel} 따라가기</span>
      </div>

      <p className="mt-1 text-xs text-fg-subtle">{MODE_DESCRIPTION[config.mode]}</p>

      {/*
        🔴 돈이 움직이는 모드면 눈에 띄게 알린다
          판정은 서버가 내려준 placesRealOrders 를 쓴다. 화면에서 모드로
          다시 계산하면 언젠가 서버와 갈린다.
      */}
      {config.placesRealOrders && (
        <p className="mt-2 rounded bg-surface-hover px-3 py-2 text-xs font-medium text-rise">
          실제 주문이 나가는 모드입니다.
        </p>
      )}

      {/*
        🔴 한도의 뜻이 2026-09-27(A3.5 게이트)에 바뀌었다 — 이름만 보고 옛 뜻으로 읽지 않게 적는다
          종목당: 한 번 주문이 아니라 이미 산 원가 + 이번 주문(사다리 여러 단의 합)
          하루 주문: 매수만 센다. 매도(손절 포함)는 한도에 막히지 않는다
          하루 손실: 판 손익 + 들고 있는 평가 손익(전일 종가 대비)
          전체 노출: 비어 있으면 "제한 없음" 이 아니라 실제 주문의 매수를 막는다
      */}
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        {/* ✏️ 9/28 — "산 금액 합" 이 아니라 판 몫을 평균 원가로 뺀 남은 보유 원가다(AutoTradeGate · AutoTradeLedger) */}
        <Limit label="종목당 한도 (들고 있는 원가)" value={formatKrw(config.maxPerStockAmount)} />
        <Limit label="하루 매수 주문" value={`${config.maxDailyOrders}건`} />
        <Limit label="하루 손실 한도 (평가 포함)" value={formatKrw(config.maxDailyLossAmount)} />
        <Limit
          label="전체 노출 한도"
          value={
            config.maxTotalExposureAmount == null
              ? '미설정 — 실제 매수 막힘'
              : formatKrw(config.maxTotalExposureAmount)
          }
        />
        <Limit
          label="조건주문"
          value={config.conditionalOrderEnabled ? '사용' : '사용 안 함'}
        />
      </dl>
      <p className="mt-2 text-xs text-fg-subtle">
        파는 주문은 한도에 막히지 않습니다. 손절을 막으면 손실이 커지기 때문입니다.
      </p>

      {config.pendingMode && (
        <p className="mt-3 text-xs text-fg-muted">
          <Badge tone="warning">예약</Badge>{' '}
          {config.pendingEffectiveDate}부터 {MODE_LABEL[config.pendingMode]}로 바뀝니다.
        </p>
      )}

      {config.lastChangeNote && (
        <p className="mt-2 text-xs text-fg-subtle">최근 변경: {config.lastChangeNote}</p>
      )}
    </Card>
  );
}

function Limit({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="font-medium text-fg-muted">{value}</dd>
    </div>
  );
}

/**
 * 🔴 폰에서는 표가 아니라 카드로 보여준다 (2026-09-20 폰 400px 확인에서 잡았다)
 *
 *   열이 일곱이라 400px 에 표로 넣으면 "매수" 가 두 줄, "한도에 막힘" 이
 *   세 줄로 쪼개진다. 가로 스크롤로 미는 방법도 있지만 그것은 U-2 에서
 *   이미 지적받은 것이다(저점매수 감시 표가 폰에서 가로로 밀렸다).
 *
 *   그래서 폰은 카드 목록, 640px 이상은 표로 가른다. 같은 데이터를 두 번
 *   그리는 대신 각 폭에서 읽을 수 있게 한다.
 */
function OrderTable({ orders }: { orders: AutoTradeOrder[] }) {
  return (
    <>
      <ul className="flex flex-col gap-2 sm:hidden">
        {orders.map((order) => (
          <li key={order.id} className="rounded border border-line p-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <StockLink symbol={order.symbol}>
                <span className="font-medium">{order.symbol}</span>
              </StockLink>
              <Badge tone={order.side === 'BUY' ? 'danger' : 'info'}>
                {order.side === 'BUY' ? '매수' : '매도'}
              </Badge>
              <Badge tone={decisionTone(order.decision)}>
                {DECISION_LABEL[order.decision]}
              </Badge>
              <span className="ml-auto text-xs text-fg-subtle">
                {formatDateTime(order.requestedAt)}
              </span>
            </div>

            <p className="mt-1 text-xs text-fg-muted">
              {order.sourceLabel} · {order.quantity.toLocaleString('ko-KR')}주
              {order.price !== null && <> · {formatKrw(order.price)}</>}
              {order.price === null && <> · 시장가</>}
            </p>

            <p className="mt-1 text-xs text-fg-subtle">{reasonOf(order)}</p>
          </li>
        ))}
      </ul>

      <div className="hidden sm:block">
        <Table>
          <thead>
            <tr>
              <Th>시각</Th>
              <Th>계좌</Th>
              <Th>종목</Th>
              <Th>사/팔</Th>
              <Th align="right">수량</Th>
              <Th align="right">가격</Th>
              <Th>결말</Th>
              <Th>이유</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <Td className="whitespace-nowrap text-xs text-fg-subtle">
                  {formatDateTime(order.requestedAt)}
                </Td>
                <Td className="whitespace-nowrap text-xs">{order.sourceLabel}</Td>
                <Td className="whitespace-nowrap">
                  <StockLink symbol={order.symbol}>{order.symbol}</StockLink>
                </Td>
                <Td>
                  <Badge tone={order.side === 'BUY' ? 'danger' : 'info'}>
                    {order.side === 'BUY' ? '매수' : '매도'}
                  </Badge>
                </Td>
                <Td className="text-right">{order.quantity.toLocaleString('ko-KR')}</Td>
                <Td className="text-right">
                  {order.price === null ? '시장가' : formatKrw(order.price)}
                </Td>
                <Td>
                  <Badge tone={decisionTone(order.decision)}>
                    {DECISION_LABEL[order.decision]}
                  </Badge>
                </Td>
                <Td className="text-xs text-fg-subtle">{reasonOf(order)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
}

/**
 * 무엇을 이유로 보여줄까.
 *
 * 🔴 막힌 이유가 있으면 그것이 더 중요하다
 *   "왜 안 샀나" 를 답하는 것이 이 화면의 핵심이다. 신호 이유는
 *   모의 계좌 화면에서도 볼 수 있지만, 막힌 이유는 여기에만 있다.
 */
function reasonOf(order: AutoTradeOrder): string {
  if (order.decisionReason && order.decision !== 'SHADOW') {
    return order.decisionReason;
  }
  return order.signalReason;
}
